import React, { useState } from "react";
import {
  Box,
  Flex,
  Button,
  Text,
  useToast,
  Tooltip,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Badge,
} from "@chakra-ui/react";
import { FaFileExcel } from "react-icons/fa";
import CustomDatePicker from "@/components/CustomDatePicker";
import {
  downloadConsigCommissionsB2C,
  downloadConsigCommissionsB2B,
  getConsigCommissions,
} from "@/services/consignado/financial-service/commission.service";
import { CommissionReport, CommissionFilters } from "@/models/commission-report.model";
import DebouncedAutocompleteInput from "@/components/FinancialShared/DebouncedAutocompleteInput";
import {
  searchPromoters,
  searchCorbans,
  searchCommercialManagers,
} from "@/services/consignado/financial-service/autocomplete.service";
import { searchVendedores } from "@/services/financial-service/global-state.service";
import {
  downloadConsigKamReport,
  getConsigKamReport,
} from "@/services/consignado/financial-service/kam.service";
import { KamReportItem } from "@/models/kam-report.model";
import EmptyState from "@/components/CommissionTab/EmptyState";
import Pagination from "@/components/FinancialShared/Pagination";
import { cpfFormatter } from "@/utils/cpfFormatter";
import { formatPhone } from "@/utils/formatPhone";

interface CommissionTabProps {
  backgroundStyle: any;
}

const defaultEndDate = new Date();
defaultEndDate.setHours(23, 59, 59, 999);

const defaultStartDate = new Date();
defaultStartDate.setDate(1);
defaultStartDate.setHours(0, 0, 0, 0);

const INITIAL_FILTERS: CommissionFilters & { commercialManager?: string } = {
  startDate: defaultStartDate.toISOString(),
  endDate: defaultEndDate.toISOString(),
  type: "b2b",
  promoterName: "",
  corbanName: "",
  sellerName: "",
  commercialManager: "",
};

interface ExtendedCommissionFilters extends CommissionFilters {
  commercialManager?: string;
  type: "b2b" | "b2c" | "kam" | "";
}

interface AutocompleteInputState {
  promoterName: string;
  corbanName: string;
  sellerName: string;
  commercialManager: string;
}

const INITIAL_AUTOCOMPLETE: AutocompleteInputState = {
  promoterName: "",
  corbanName: "",
  sellerName: "",
  commercialManager: "",
};

const KAM_COLUMNS = [
  { key: "nomeConsultor", label: "Nome Consultor" },
  { key: "usuarioLogin", label: "Usuário (acesso login)" },
  { key: "emailConsultor", label: "E-mail Consultor" },
  { key: "cpfConsultor", label: "CPF Consultor" },
  { key: "dataOperacao", label: "Data da Operação" },
  { key: "numeroProposta", label: "Número da Proposta" },
  { key: "nomeCorban", label: "Nome do Corban" },
  { key: "codigoCorban", label: "Código do Corban" },
  { key: "promotora", label: "Promotora" },
  { key: "gerenteComercial", label: "Gerente Comercial" },
  { key: "nomeCliente", label: "Nome do Cliente" },
  { key: "cpfCliente", label: "CPF do Cliente" },
  { key: "telefoneCliente", label: "Telefone do Cliente" },
  { key: "valorLiberado", label: "Valor Liberado" },
  { key: "meses", label: "Meses" },
  { key: "bancarizadora", label: "Bancarizadora" },
  { key: "corretora", label: "Corretora" },
  { key: "nomeProduto", label: "Nome do Produto" },
  { key: "numeroContratoBancarizadora", label: "Número de Contrato na Bancarizadora" },
  { key: "status", label: "Status" },
  { key: "dataDesembolso", label: "Data do Desembolso" },
  { key: "observacao", label: "Observação" },
  { key: "percentualComissao", label: "% Comissão Paga" },
  { key: "comissaoTotal", label: "Comissão Total" },
];

const B2B_COLUMNS = [
  { key: "nomeConsultor", label: "Nome Consultor" },
  { key: "usuarioLogin", label: "Usuário (acesso login)" },
  { key: "emailConsultor", label: "E-mail Consultor" },
  { key: "cpfConsultor", label: "CPF Consultor" },
  { key: "dataOperacao", label: "Data da Operação" },
  { key: "numeroProposta", label: "Número da Proposta" },
  { key: "corbanMaster", label: "Corban Master" },
  { key: "codigoCorban", label: "Código do Corban" },
  { key: "splitPagamento", label: "Split Pagamento" },
  { key: "promotora", label: "Promotora" },
  { key: "nomeCliente", label: "Nome do Cliente" },
  { key: "cpfCliente", label: "CPF do Cliente" },
  { key: "telefoneCliente", label: "Telefone do Cliente" },
  { key: "bancarizadora", label: "Bancarizadora" },
  { key: "corretora", label: "Corretora" },
  { key: "nomeProduto", label: "Nome do Produto" },
  { key: "valorLiberado", label: "Valor Liberado" },
  { key: "meses", label: "Meses" },
  { key: "numeroContratoBancarizadora", label: "Número de Contrato na Bancarizadora" },
  { key: "status", label: "Status" },
  { key: "dataDesembolso", label: "Data do Desembolso" },
  { key: "observacao", label: "Observação" },
  { key: "percentualComissao", label: "% Comissão Paga" },
  { key: "comissaoBruta", label: "Comissão Bruta" },
  { key: "csll", label: "CSLL" },
  { key: "irrf", label: "IRRF" },
  { key: "cofins", label: "COFINS" },
  { key: "pis", label: "PIS" },
  { key: "comissaoLiquida", label: "Comissão Líquida" },
];

const B2C_COLUMNS = [
  { key: "nomeConsultor", label: "Nome Consultor" },
  { key: "usuarioLogin", label: "Usuário (acesso login)" },
  { key: "emailConsultor", label: "E-mail Consultor" },
  { key: "cpfConsultor", label: "CPF Consultor" },
  { key: "dataOperacao", label: "Data da Operação" },
  { key: "numeroProposta", label: "Número da Proposta" },
  { key: "corbanMaster", label: "Corban Master" },
  { key: "codigoCorban", label: "Código do Corban" },
  { key: "splitPagamento", label: "Split Pagamento" },
  { key: "promotora", label: "Promotora" },
  { key: "nomeCliente", label: "Nome do Cliente" },
  { key: "cpfCliente", label: "CPF do Cliente" },
  { key: "telefoneCliente", label: "Telefone do Cliente" },
  { key: "bancarizadora", label: "Bancarizadora" },
  { key: "corretora", label: "Corretora" },
  { key: "nomeProduto", label: "Nome do Produto" },
  { key: "valorLiberado", label: "Valor Liberado" },
  { key: "meses", label: "Meses" },
  { key: "numeroContratoBancarizadora", label: "Número de Contrato na Bancarizadora" },
  { key: "status", label: "Status" },
  { key: "dataDesembolso", label: "Data do Desembolso" },
  { key: "observacao", label: "Observação" },
  { key: "percentualComissao", label: "% Comissão Paga" },
  { key: "comissaoTotal", label: "Comissão Total" },
];

const formatDate = (date: string | Date | null): string => {
  if (!date) return "-";
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(dateObj);
};

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "R$ 0,00";
  const isNegative = value < 0;
  const absValue = Math.abs(value);
  const formatted = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(absValue);
  return isNegative ? `-${formatted}` : formatted;
};

const getCommercialManager = (item: any): string => {
  const fsCp = item.credit_promoter?.FinancialSettings;
  const cpManager = fsCp?.CommercialManager;
  if (cpManager?.name) {
    return `${cpManager.name} ${cpManager.lastName || ""}`.trim();
  }
  if (fsCp?.externalCommercialManager) {
    return fsCp.externalCommercialManager;
  }

  const fsCorban = item.corban?.FinancialSettings;
  const corbanManager = fsCorban?.CommercialManager;
  if (corbanManager?.name) {
    return `${corbanManager.name} ${corbanManager.lastName || ""}`.trim();
  }
  if (fsCorban?.externalCommercialManager) {
    return fsCorban.externalCommercialManager;
  }

  return "GranaTech";
};

const mapApiToKamReport = (item: any): KamReportItem => {
  // Se a API já retornou os dados mapeados, use-os diretamente
  if (item.nomeConsultor) {
    // Verifica se o percentualComissao precisa ser formatado
    if (item.percentualComissao && typeof item.percentualComissao === 'number') {
      console.log('🔍 KAM percentualComissao (número):', item.percentualComissao, '→ Formatado:', `${(item.percentualComissao * 100).toFixed(2)}%`);
      return {
        ...item,
        percentualComissao: `${(item.percentualComissao * 100).toFixed(2)}%`
      } as KamReportItem;
    }
    console.log('✅ KAM percentualComissao (string):', item.percentualComissao);
    return item as KamReportItem;
  }
  
  // Caso contrário, faça o mapeamento dos campos antigos
  const percentual = item.corban_comission 
    ? `${(item.corban_comission * 100).toFixed(2)}%` 
    : "-";
  console.log('🔄 KAM mapeamento antigo - corban_comission:', item.corban_comission, '→ Formatado:', percentual);
  
  return {
    nomeConsultor: item.seller?.name
      ? `${item.seller.name} ${item.seller.lastName || ""}`.trim()
      : item.proposal_consult_name || "-",
    usuarioLogin: item.proposal_consult_id || "-",
    emailConsultor: item.seller?.email || "-",
    cpfConsultor: item.seller?.document || "-",
    dataOperacao: item.proposal_operation_date,
    numeroProposta: item.proposal_number,
    nomeCorban: item.corban_master || "-",
    codigoCorban: item.corban_id,
    promotora: item.credit_promoter_name || "-",
    gerenteComercial: getCommercialManager(item),
    nomeCliente: item.natural_person_name,
    cpfCliente: item.natural_person_document,
    telefoneCliente: item.natural_person_phone,
    valorLiberado: item.released_amount,
    meses: item.months,
    bancarizadora: item.processor_name,
    corretora: item.insurance_company_name,
    nomeProduto: item.product_name,
    numeroContratoBancarizadora: item.ccb_number,
    status: item.proposal_status,
    dataDesembolso: item.disbursement_date,
    observacao: item.observation || null,
    percentualComissao: percentual,
    comissaoTotal: item.liquid_comission,
  };
};

const mapApiToCommissionReport = (item: any): CommissionReport => {
  // Se a API já retornou os dados mapeados, use-os diretamente
  if (item.nomeConsultor && item.percentualComissao && typeof item.percentualComissao === 'string') {
    return item as CommissionReport;
  }
  
  // Se veio como número, formata
  if (item.nomeConsultor && item.percentualComissao && typeof item.percentualComissao === 'number') {
    return {
      ...item,
      percentualComissao: `${(item.percentualComissao * 100).toFixed(2)}%`
    } as CommissionReport;
  }
  
  // Caso contrário, faça o mapeamento dos campos antigos
  const taxes =
    item.credit_promoter?.FinancialSettings?.FinancialSettingsTax || [];

  const getTax = (name: string) =>
    taxes.find((t: any) => t.name === name)?.percentage || 0;

  const comissaoBruta = item.released_amount && item.corban_comission
    ? item.released_amount * item.corban_comission
    : 0;

  const irrf = getTax("IRRF");
  const cofins = getTax("COFINS");
  const pis = getTax("PIS");
  const csll = getTax("CSLL");

  return {
    nomeConsultor: item.seller?.name
      ? `${item.seller.name} ${item.seller.lastName || ""}`.trim()
      : item.proposal_consult_name || "-",
    usuarioLogin: item.proposal_consult_id || "-",
    emailConsultor: item.seller?.email || null,
    cpfConsultor: item.seller?.document || "-",
    dataOperacao: item.proposal_operation_date,
    numeroProposta: item.proposal_number,
    corbanMaster: item.corban_master,
    codigoCorban: item.corban_id,
    splitPagamento: item.corban?.paymentSplit ? "sim" : "",
    promotora: item.credit_promoter_name || "-",
    nomeCliente: item.natural_person_name,
    cpfCliente: item.natural_person_document,
    telefoneCliente: item.natural_person_phone,
    bancarizadora: item.processor_name,
    corretora: item.insurance_company_name,
    nomeProduto: item.product_name,
    valorLiberado: item.released_amount,
    meses: item.months,
    numeroContratoBancarizadora: item.ccb_number,
    status: item.proposal_status,
    dataDesembolso: item.disbursement_date,
    observacao: null,
    percentualComissao: item.corban_comission
      ? `${(item.corban_comission * 100).toFixed(2)}%`
      : "-",
    comissaoBruta: comissaoBruta,
    csll: comissaoBruta * csll * -1,
    irrf: comissaoBruta * irrf * -1,
    cofins: comissaoBruta * cofins * -1,
    pis: comissaoBruta * pis * -1,
    comissaoLiquida: item.liquid_comission || 0,
  };
};

const renderCurrency = (value: number | undefined | null, isNegativeField: boolean = false) => {
  if (typeof value !== "number") return "-";
  
  const isNegative = value < 0;
  
  // Apenas valores negativos em vermelho (impostos)
  // Comissões bruta e líquida ficam em cinza
  let color = "gray.800";
  if (isNegative && isNegativeField) color = "red.500";
  
  return (
    <Text
      color={color}
      fontWeight={isNegative && isNegativeField ? "500" : "normal"}
      whiteSpace="nowrap"
      display="flex"
      justifyContent="flex-end"
      width="100%"
      lineHeight="1.4"
    >
      {formatCurrency(value)}
    </Text>
  );
};

const renderStatus = (status: string | undefined) => {
  if (!status) return "-";
  
  // Mapeamento de status
  const statusMap: { [key: string]: { label: string; colorScheme: string } } = {
    PAYED: { label: "PAGO", colorScheme: "green" },
    PAID: { label: "PAGO", colorScheme: "green" },
    PENDING: { label: "PENDENTE", colorScheme: "yellow" },
    CANCELLED: { label: "CANCELADO", colorScheme: "red" },
    REJECTED: { label: "REJEITADO", colorScheme: "red" },
  };
  
  const statusInfo = statusMap[status.toUpperCase()] || { 
    label: status, 
    colorScheme: "gray" 
  };
  
  return (
    <Badge 
      colorScheme={statusInfo.colorScheme}
      fontSize="xs"
      px={3}
      py={1}
      borderRadius="md"
      textTransform="uppercase"
      fontWeight="bold"
    >
      {statusInfo.label}
    </Badge>
  );
};

export default function ConsigCommissionTab({ backgroundStyle }: CommissionTabProps) {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    defaultStartDate,
    defaultEndDate,
  ]);
  const [filters, setFilters] = useState<ExtendedCommissionFilters>(INITIAL_FILTERS);
  const [autocompleteInputs, setAutocompleteInputs] =
    useState<AutocompleteInputState>(INITIAL_AUTOCOMPLETE);

  const [data, setData] = useState<any[]>([]);
  const [meta, setMeta] = useState({
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    itemsPerPage: 5,
  });
  const [pageSize, setPageSize] = useState(5);
  const [isQuerying, setIsQuerying] = useState(false);
  const [isDownloadingB2C, setIsDownloadingB2C] = useState(false);
  const [isDownloadingB2B, setIsDownloadingB2B] = useState(false);
  const [isDownloadingKam, setIsDownloadingKam] = useState(false);
  const [hasQueried, setHasQueried] = useState(false);
  const toast = useToast();

  React.useEffect(() => {
    handleQuery(1, pageSize);
  }, []);

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    setDateRange(dates);
    setFilters((prev) => ({
      ...prev,
      startDate: dates[0]?.toISOString() ?? "",
      endDate: dates[1]?.toISOString() ?? "",
    }));
  };

  const handleQuery = async (page = 1, requestedPageSize = pageSize) => {
    if (!filters.startDate || !filters.endDate) {
      toast({
        title: "Selecione o intervalo de datas",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    if (!filters.type) {
      toast({
        title: "Selecione o tipo do relatório",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setIsQuerying(true);

    try {
      const cleanedFilters = {
        ...filters,
        promoterName: filters.promoterName?.trim() || undefined,
        corbanName: filters.corbanName?.trim() || undefined,
        sellerName: filters.sellerName?.trim() || undefined,
        commercialManager: filters.commercialManager?.trim() || undefined,
      };

      let result;
      if (filters.type === "kam") {
        result = await getConsigKamReport(cleanedFilters as any, page, requestedPageSize);
        console.log("🔍 KAM Raw Data:", result.items);
        const mappedData = result.items.map(mapApiToKamReport);
        console.log("✅ KAM Mapped Data:", mappedData);
        setData(mappedData);
      } else {
        result = await getConsigCommissions(cleanedFilters, page, requestedPageSize);
        console.log("🔍 B2B/B2C Raw Data:", result.items);
        const mappedData = result.items.map(mapApiToCommissionReport);
        console.log("✅ B2B/B2C Mapped Data:", mappedData);
        setData(mappedData);
      }

      if (result) {
        setMeta({
          totalItems: result.totalItems,
          totalPages: result.totalPages,
          currentPage: result.currentPage,
          itemsPerPage: result.pageSize,
        });
        setHasQueried(true);
      }
    } catch (error: any) {
      toast({
        title: "Erro ao consultar relatórios",
        description: `${error}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsQuerying(false);
    }
  };

  const checkHasData = async (type: "b2c" | "b2b" | "kam"): Promise<boolean> => {
    if (!dateRange[0] || !dateRange[1]) {
      toast({
        title: "Selecione o intervalo de datas",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return false;
    }

    const startDate = dateRange[0].toISOString();
    const endDate = dateRange[1].toISOString();

    try {
      const result =
        type === "kam"
          ? await getConsigKamReport({ startDate, endDate }, 1, 1)
          : await getConsigCommissions({ startDate, endDate, type }, 1, 1);

      if (!result?.totalItems || result.totalItems === 0) {
        toast({
          title: "Nenhum relatório encontrado para o intervalo de datas selecionado.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
        return false;
      }

      return true;
    } catch (error: any) {
      toast({
        title: "Erro ao consultar relatórios",
        description: `${error}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
  };

  const handleClearFilters = () => {
    setDateRange([null, null]);
    setFilters(INITIAL_FILTERS);
    setAutocompleteInputs(INITIAL_AUTOCOMPLETE);
    setData([]);
    setHasQueried(false);
  };

  const handleDownloadB2C = async () => {
    if (!dateRange[0] || !dateRange[1]) {
      toast({
        title: "Selecione o intervalo de datas",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    const hasData = await checkHasData("b2c");
    if (!hasData) return;

    setIsDownloadingB2C(true);
    try {
      await downloadConsigCommissionsB2C(
        dateRange[0].toISOString(),
        dateRange[1].toISOString(),
        [3]
      );
      toast({
        title: "Relatório B2C gerado com sucesso",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: "Erro ao gerar relatório B2C",
        description: `${error}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDownloadingB2C(false);
    }
  };

  const handleDownloadB2B = async () => {
    if (!dateRange[0] || !dateRange[1]) {
      toast({
        title: "Selecione o intervalo de datas",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    const hasData = await checkHasData("b2b");
    if (!hasData) return;

    setIsDownloadingB2B(true);
    try {
      await downloadConsigCommissionsB2B(
        dateRange[0].toISOString(),
        dateRange[1].toISOString(),
        [3]
      );
      toast({
        title: "Relatório B2B gerado com sucesso",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: "Erro ao gerar relatório B2B",
        description: `${error}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDownloadingB2B(false);
    }
  };

  const handleDownloadKam = async () => {
    if (!dateRange[0] || !dateRange[1]) {
      toast({
        title: "Selecione o intervalo de datas",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    const hasData = await checkHasData("kam");
    if (!hasData) return;

    setIsDownloadingKam(true);
    try {
      await downloadConsigKamReport(
        dateRange[0].toISOString(),
        dateRange[1].toISOString()
      );
      toast({
        title: "Relatório KAM gerado com sucesso",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: "Erro ao gerar relatório KAM",
        description: `${error}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDownloadingKam(false);
    }
  };

  const totalPages = meta.totalPages || 1;
  const currentColumns = filters.type === "kam" ? KAM_COLUMNS : filters.type === "b2b" ? B2B_COLUMNS : B2C_COLUMNS;

  return (
    <Box width="100%">
      {/* FILTROS */}
      <Box
        {...backgroundStyle}
        borderRadius="xl"
        p={{ base: 4, md: 6 }}
        color="white"
        boxShadow="lg"
        width="100%"
        mb={4}
        h="auto"
      >
        <Flex justifyContent="space-between" alignItems="center" mb={6} flexWrap="wrap" gap={4}>
          <Text fontSize="xl" fontWeight="bold" fontFamily="Lexend Deca">
            FILTROS DE COMISSÃO CLT
          </Text>
          <Flex gap={3}>
            <Button
              color="white"
              colorScheme="green"
              textTransform="uppercase"
              leftIcon={<FaFileExcel />}
              onClick={handleDownloadB2C}
              isLoading={isDownloadingB2C}
              isDisabled={isDownloadingB2B || isDownloadingKam}
              borderRadius="lg"
              height="2.8rem"
              px={6}
              boxShadow="sm"
              fontSize="sm"
            >
              B2C
            </Button>
            <Button
              color="white"
              colorScheme="green"
              textTransform="uppercase"
              leftIcon={<FaFileExcel />}
              onClick={handleDownloadB2B}
              isLoading={isDownloadingB2B}
              isDisabled={isDownloadingB2C || isDownloadingKam}
              borderRadius="lg"
              height="2.8rem"
              px={6}
              boxShadow="sm"
              fontSize="sm"
            >
              B2B
            </Button>
            <Button
              color="white"
              colorScheme="green"
              textTransform="uppercase"
              leftIcon={<FaFileExcel />}
              onClick={handleDownloadKam}
              isLoading={isDownloadingKam}
              isDisabled={isDownloadingB2C || isDownloadingB2B}
              borderRadius="lg"
              height="2.8rem"
              px={6}
              boxShadow="sm"
              fontSize="sm"
            >
              KAM
            </Button>
          </Flex>
        </Flex>

        <Flex flexDirection="column" gap={6}>
          <Flex gap={4} flexWrap="wrap" alignItems="flex-end">
            {/* Data */}
            <Box flex={1} minW="160px">
              <CustomDatePicker
                dateRange={dateRange}
                onChange={handleDateChange}
              />
            </Box>

            {/* Tipo Relatório */}
            <Box flex={1} maxW="200px">
              <Tooltip label="Tipo do relatório" fontSize="md">
                <Select
                  bg="white"
                  color={filters.type ? "black" : "gray.500"}
                  height="3rem"
                  fontFamily="Lexend Deca"
                  placeholder="selecionar tipo"
                  value={filters.type}
                  onChange={(e) => {
                    const newType = e.target.value as "b2b" | "b2c" | "kam";
                    setFilters((prev) => ({ ...prev, type: newType }));
                    setData([]);
                    setHasQueried(false);
                  }}
                  borderRadius="lg"
                >
                  <option value="b2c">B2C</option>
                  <option value="b2b">B2B</option>
                  <option value="kam">KAM</option>
                </Select>
              </Tooltip>
            </Box>

            {/* Promotora — autocomplete */}
            <DebouncedAutocompleteInput
              placeholder="Filtrar por promotora"
              tooltip="Filtro de promotora por nome fantasia"
              inputValue={autocompleteInputs.promoterName}
              onInputChange={(val) => {
                setAutocompleteInputs((prev) => ({ ...prev, promoterName: val }));
                setFilters((prev) => ({ ...prev, promoterName: "" }));
              }}
              onSelect={(label) => {
                setAutocompleteInputs((prev) => ({ ...prev, promoterName: label }));
                setFilters((prev) => ({ ...prev, promoterName: label }));
              }}
              onClear={() => {
                setAutocompleteInputs((prev) => ({ ...prev, promoterName: "" }));
                setFilters((prev) => ({ ...prev, promoterName: "" }));
              }}
              fetchOptions={searchPromoters}
            />

            {/* Corban — autocomplete */}
            <DebouncedAutocompleteInput
              placeholder="Filtrar por Corban"
              tooltip="Filtro de corban por nome fantasia"
              inputValue={autocompleteInputs.corbanName}
              onInputChange={(val) => {
                setAutocompleteInputs((prev) => ({ ...prev, corbanName: val }));
                setFilters((prev) => ({ ...prev, corbanName: "" }));
              }}
              onSelect={(label) => {
                setAutocompleteInputs((prev) => ({ ...prev, corbanName: label }));
                setFilters((prev) => ({ ...prev, corbanName: label }));
              }}
              onClear={() => {
                setAutocompleteInputs((prev) => ({ ...prev, corbanName: "" }));
                setFilters((prev) => ({ ...prev, corbanName: "" }));
              }}
              fetchOptions={searchCorbans}
            />

            {/* Vendedor — autocomplete */}
            <Box flex={1} minW="200px">
              <DebouncedAutocompleteInput
                placeholder="Filtrar por Vendedor"
                tooltip="Filtro de vendedor por nome"
                inputValue={autocompleteInputs.sellerName}
                onInputChange={(val) => {
                  setAutocompleteInputs((prev) => ({ ...prev, sellerName: val }));
                  setFilters((prev) => ({ ...prev, sellerName: "" }));
                  setHasQueried(false);
                }}
                onSelect={(label) => {
                  setAutocompleteInputs((prev) => ({ ...prev, sellerName: label }));
                  setFilters((prev) => ({ ...prev, sellerName: label }));
                  setHasQueried(false);
                }}
                onClear={() => {
                  setAutocompleteInputs((prev) => ({ ...prev, sellerName: "" }));
                  setFilters((prev) => ({ ...prev, sellerName: "" }));
                  setHasQueried(false);
                }}
                fetchOptions={searchVendedores}
              />
            </Box>

            {/* Gerente Comercial — autocomplete (B2B, B2C e KAM) */}
            <DebouncedAutocompleteInput
              placeholder="Gerente Comercial"
              tooltip="Filtro de gerente comercial por nome"
              inputValue={autocompleteInputs.commercialManager}
              onInputChange={(val) => {
                setAutocompleteInputs((prev) => ({
                  ...prev,
                  commercialManager: val,
                }));
                setFilters((prev) => ({ ...prev, commercialManager: "" }));
              }}
              onSelect={(label) => {
                setAutocompleteInputs((prev) => ({
                  ...prev,
                  commercialManager: label,
                }));
                setFilters((prev) => ({ ...prev, commercialManager: label }));
              }}
              onClear={() => {
                setAutocompleteInputs((prev) => ({
                  ...prev,
                  commercialManager: "",
                }));
                setFilters((prev) => ({ ...prev, commercialManager: "" }));
              }}
              fetchOptions={searchCommercialManagers}
            />
          </Flex>

          <Flex
            justifyContent="flex-end"
            alignItems="center"
            flexWrap="wrap"
            gap={4}
          >
            <Flex gap={3}>
              <Button
                variant="ghost"
                color="white"
                _hover={{ opacity: 0.8 }}
                onClick={handleClearFilters}
                height="2.8rem"
                px={6}
              >
                Limpar Filtros
              </Button>
              <Button
                bg="primary"
                color="white"
                _hover={{ opacity: 0.8 }}
                onClick={() => handleQuery(1)}
                isLoading={isQuerying}
                isDisabled={
                  !filters.type || !filters.startDate || !filters.endDate
                }
                borderRadius="lg"
                height="2.8rem"
                px={8}
                boxShadow="lg"
                fontWeight="bold"
              >
                Filtrar
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Box>

      {/* ESTADO INICIAL */}
      {!hasQueried && !isQuerying ? (
        <EmptyState mode="initial" />
      ) : isQuerying ? (
        /* LOADING */
        <Box
          w="100%"
          p={{ base: 4, md: 6 }}
          borderWidth="1px"
          borderRadius="xl"
          boxShadow="lg"
          bg="white"
        >
          <Flex
            justifyContent="center"
            alignItems="center"
            minH="400px"
          >
            <Spinner size="xl" color="primary" thickness="4px" />
          </Flex>
        </Box>
      ) : hasQueried && data.length === 0 ? (
        /* SEM RESULTADO */
        <EmptyState mode="no-results" onClearFilters={handleClearFilters} />
      ) : (
        /* TABELA */
        <Box
          w="100%"
          borderWidth="1px"
          borderRadius="xl"
          boxShadow="lg"
          bg="white"
        >
          <Box
            overflowX="auto"
            sx={{
              "&::-webkit-scrollbar": {
                height: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#CBD5E0",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#A0AEC0",
              },
            }}
          >
          <Table
            size="sm"
            w="100%"
            minW="max-content"
            variant="simple"
            style={{ borderCollapse: "separate", borderSpacing: 0 }}
          >
            <Thead
              {...backgroundStyle}
              boxShadow="md"
              position="sticky"
              top={0}
              zIndex={2}
            >
              <Tr>
                {currentColumns.map((col, index) => (
                  <Th
                    key={col.key}
                    borderTopLeftRadius={index === 0 ? "xl" : "0"}
                    borderTopRightRadius={index === currentColumns.length - 1 ? "xl" : "0"}
                    color="white"
                    textAlign="center"
                    fontSize="xs"
                    fontWeight="bold"
                    py={5}
                    px={4}
                    whiteSpace="nowrap"
                    textTransform="uppercase"
                  >
                    {col.label}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {data.map((row, i) => {
                console.log(`📊 Renderizando linha ${i}:`, row);
                return (
                <Tr
                  key={i}
                  _hover={{ bg: "teal.50" }}
                  borderBottom="1px solid"
                  borderColor="gray.200"
                >
                  <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.nomeConsultor || "-"}</Td>
                  <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.usuarioLogin || "-"}</Td>
                  <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.emailConsultor || "-"}</Td>
                      <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.cpfConsultor ? cpfFormatter(row.cpfConsultor) : "-"}</Td>
                      <Td textAlign="center" fontSize="sm" py={5} px={4}>{formatDate(row.dataOperacao)}</Td>
                      <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.numeroProposta || "-"}</Td>

                      {filters.type === "kam" ? (
                        <>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.nomeCorban || "-"}</Td>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.codigoCorban ?? "-"}</Td>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.promotora || "-"}</Td>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.gerenteComercial || "-"}</Td>
                        </>
                      ) : (
                        <>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.corbanMaster || "-"}</Td>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.codigoCorban ?? "-"}</Td>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.splitPagamento || "-"}</Td>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.promotora || "-"}</Td>
                        </>
                      )}

                      <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.nomeCliente || "-"}</Td>
                      <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.cpfCliente ? cpfFormatter(row.cpfCliente) : "-"}</Td>
                      <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.telefoneCliente ? formatPhone(row.telefoneCliente) : "-"}</Td>

                      {filters.type === "kam" ? (
                        <>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{formatCurrency(row.valorLiberado)}</Td>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.meses ?? "-"}</Td>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.bancarizadora || "-"}</Td>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.corretora || "-"}</Td>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.nomeProduto || "-"}</Td>
                        </>
                      ) : (
                        <>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.bancarizadora || "-"}</Td>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.corretora || "-"}</Td>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.nomeProduto || "-"}</Td>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{formatCurrency(row.valorLiberado)}</Td>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.meses ?? "-"}</Td>
                        </>
                      )}

                      <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.numeroContratoBancarizadora || "-"}</Td>
                      <Td textAlign="center" fontSize="sm" py={5} px={4}>{renderStatus(row.status)}</Td>
                      <Td textAlign="center" fontSize="sm" py={5} px={4}>{formatDate(row.dataDesembolso)}</Td>
                      <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.observacao || "-"}</Td>
                      <Td textAlign="center" fontSize="sm" py={5} px={4}>{row.percentualComissao || "-"}</Td>

                      {filters.type === "kam" ? (
                        <Td textAlign="center" fontSize="sm" py={5} px={4}>{renderCurrency(row.comissaoTotal, false)}</Td>
                      ) : filters.type === "b2c" ? (
                        <Td textAlign="center" fontSize="sm" py={5} px={4}>{renderCurrency(row.comissaoTotal, false)}</Td>
                      ) : (
                        <>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{renderCurrency(row.comissaoBruta, false)}</Td>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{renderCurrency(row.csll, true)}</Td>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{renderCurrency(row.irrf, true)}</Td>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{renderCurrency(row.cofins, true)}</Td>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{renderCurrency(row.pis, true)}</Td>
                          <Td textAlign="center" fontSize="sm" py={5} px={4}>{renderCurrency(row.comissaoLiquida, false)}</Td>
                        </>
                      )}
                    </Tr>
                  );
                  })}
              </Tbody>
            </Table>

          </Box>

          {/* PAGINAÇÃO */}
          <Pagination
            currentPage={meta.currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            isQuerying={isQuerying}
            onPageChange={(page) => handleQuery(page, pageSize)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              handleQuery(1, size);
            }}
          />
        </Box>
      )}
    </Box>
  );
}
